import { API_URL } from "./../../General/APIEndpoints";
import { FetchError } from "./../../General/ErrorTypes";
import { CoreStore, handleStoreState } from "Stores/RESTStore/CoreStore";
import { observable, action, runInAction, reaction, makeObservable } from "mobx";
import { API } from "General/API";
import { projectsDataStore } from "Stores";
import { download } from "utils/download";
import { webDavStore } from "./WebDAVStore";
import { notificationError } from "utils/libHelpers/AntdNotification";
import i18n from "Config/i18n";

export interface TaskRelationInfo extends Omit<API_RelationInfo, "task">, API_TaskData {}
export interface WorkflowRelationInfo extends Omit<API_RelationInfo, "workflow">, API_WorkflowInfo {}
export interface QuestionRelationInfo extends Omit<API_RelationInfo, "question">, API_Question {}
type Relations = {
  [key in API_FILEDATAENTITY_RELATIONS]: (
    | (API_RelationInfo & { idx?: number })
    | (TaskRelationInfo & { idx?: number })
    | (QuestionRelationInfo & { idx?: number })
    | (WorkflowRelationInfo & { idx?: number })
  )[];
};

interface InitModalOptions {
  from?: "dashboard" | "project" | "collection_folder" | "documents";
  linkId?: number;
}

export class FileDetailsStore extends CoreStore {
  /**
   * Because FileDetailsModal can be used beyond project (normally it should be used within certain project),
   * but it needs project id set up to call endpoints we set project id before init modal and clear it with MobX
   * reaction on modal close. "from = 'dashboard'" - reset project when modal closes and show only
   * dashboard-allowed actions for file. "from = 'project" - regular mode when file details is called within project.
   */
  from: "dashboard" | "project" | "documents" | "collection_folder" = "project";
  isVisible = false;
  fileId: number = null;
  folderId: number = null;
  filename: string = "";
  previewError: boolean = false;
  notarizations: API_DocnodesFileInfo = null;
  fileRelations: Relations = {
    MASTER: [],
    SLAVE: [],
    SYMLINK: [],
    WORKFLOW: [],
    TASK: [],
    QUESTION: [],
    ORIGINAL: [],
  };
  data: API_FileDataEntity = {};
  private opts: InitModalOptions = {};
  constructor() {
    super();

    makeObservable(this, {
      from: observable,
      isVisible: observable,
      fileId: observable,
      folderId: observable,
      filename: observable,
      previewError: observable,
      fileRelations: observable,
      notarizations: observable,
      data: observable,
      clear: action.bound,
      toggleModal: action.bound,
      hideModal: action.bound,
      unlockFile: action.bound,
      lockFile: action.bound,
      downloadAsPdf: action.bound,
      download: action.bound,
      fetchPreview: action.bound,
      fetchRelations: action.bound,
      fetchNotarization: action.bound,
      verifyNotarization: action.bound,
      deleteSymlink: action.bound,
      editFileDetails: action.bound,
      openWebdavLink: action.bound,
      setFilename: action.bound,
      handleError: action.bound,
      handlePreviewError: action.bound,
      initModal: action.bound,
      fetchLabelsPreview: action.bound,
      fetch: action.bound,
      refresh: action.bound,
    });
  }

  url() {
    return "";
  }

  clear() {
    this.from = "project";
    this.isVisible = false;
    this.folderId = undefined;
    this.fileId = undefined;
    this.filename = "";
    this.data = {};
    this.previewError = false;
    this.notarizations = null;
    this.opts = {};
  }

  toggleModal() {
    this.isVisible = !this.isVisible;
    if (!this.isVisible) {
      this.clear();
    }
  }

  hideModal() {
    this.isVisible = false;
    this.clear();
  }

  @handleStoreState()
  async initModal(
    folderId: number,
    fileId: number,
    opts: InitModalOptions = {
      from: "project",
    }
  ) {
    console.log("initModal", folderId, fileId, opts);
    this.from = opts.from;
    this.folderId = folderId;
    this.fileId = fileId;
    this.opts = opts;
    this.isVisible = true;
    await this.fetch();
  }

  async fetch() {
    try {
      let url: string;
      if (this.opts.linkId) {
        url = API_URL.LINKED.readLinkedFile(projectsDataStore.projectId, this.opts.linkId, this.fileId);
      } else {
        url = API_URL.DOCUMENTS.getDocumentByFileId(projectsDataStore.projectId, this.folderId, this.fileId);
      }
      const response = await API.getData(url);
      runInAction(() => {
        this.data = response;
        this.setFilename(response.name);
      });
    } catch (error) {
      if (error.response?.status === 400 && error.response.data) {
        const code = error.response.data.code;
        const title = i18n.t("Error");
        let message = "";
        if (code === 602) {
          message = i18n.t("The target does not exist");
        }
        notificationError(title, message);
      } else {
        throw error;
      }
    }
  }

  refresh() {
    return this.fetch();
  }

  async unlockFile(file?: API_FileDataEntity) {
    try {
      let folderId: number, fileId: number;
      if (file) {
        folderId = file.parentId;
        fileId = file.id;
      } else {
        folderId = this.folderId;
        fileId = this.fileId;
      }
      await API.deleteData(API_URL.FILES.unlockFiles(projectsDataStore.projectId, folderId, fileId), {});
      const response = await API.getData(
        API_URL.DOCUMENTS.getDocumentByFileId(projectsDataStore.projectId, folderId, fileId)
      );
      runInAction(() => {
        this.data = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async lockFile(file?: API_FileDataEntity) {
    try {
      let folderId: number, fileId: number;
      if (file) {
        folderId = file.parentId;
        fileId = file.id;
      } else {
        folderId = this.folderId;
        fileId = this.fileId;
      }
      await API.newData(API_URL.FILES.lockFile(projectsDataStore.projectId, folderId, fileId), {});
      const response = await API.getData(
        API_URL.DOCUMENTS.getDocumentByFileId(projectsDataStore.projectId, folderId, fileId)
      );
      runInAction(() => {
        this.data = response;
      });
    } catch (error) {
      throw error;
    }
  }

  async downloadAsPdf(id?: number) {
    try {
      const file: File = await API.getBlob(
        API_URL.VIEWER.getEasyViewerFile_2(projectsDataStore.projectId, id || this.fileId)
      );
      download(file, file.name, file.type);
    } catch (error) {
      throw error;
    }
  }

  async download(id?: number) {
    try {
      const file: File = await API.getBlob(API_URL.FILES.getFileById(projectsDataStore.projectId, id || this.fileId));
      download(file, file.name, file.type);
    } catch (error) {
      throw error;
    }
  }

  async fetchPreview(id?: number) {
    return await API.getBlob(API_URL.FILES.getFilePreview(projectsDataStore.projectId, id || this.fileId));
  }

  async fetchRelations(_id?: number) {
    const response = await API.getData<API_RelationInfo[]>(
      API_URL.FILES.getRelationInfoWithDirecotry(projectsDataStore.projectId, this.fileId, this.folderId)
    );
    if (response) {
      runInAction(() => {
        this.fileRelations = response.reduce(
          (buffer, item, idx) => {
            buffer[item.type].push({
              ...item,
              ...item[item.type.toLowerCase()],
              idx: idx,
            });
            return buffer;
          },
          {
            MASTER: [],
            SLAVE: [],
            SYMLINK: [],
            WORKFLOW: [],
            TASK: [],
            QUESTION: [],
            ORIGINAL: [],
          }
        ) as Relations;
      });
    }
  }

  async fetchNotarization(id = this.data.id) {
    const response = await API.getData<API_DocnodesFileInfo>(
      API_URL.DOCNODES.getDocNodesInfo(projectsDataStore.projectId, id)
    );
    if (response) {
      runInAction(() => {
        this.notarizations = response;
      });
    }
  }

  async verifyNotarization(url: string) {
    const response = await API.getData(url);
    if (response) {
      console.log(response);
      runInAction(() => {
        // this.notarizations = null;
      });
    }
  }

  async fetchLabelsPreview(id: number, type: "file" | "folder" = "file") {
    if (type === "file") {
      return await API.getData<API_DataLabel[]>(
        API_URL.AUTO_ORGANISER.getLabelsFromFile(projectsDataStore.projectId, id)
      );
    } else {
      /**
       * Only for files
       * https://pmgredesign.atlassian.net/browse/DEV-7103?focusedCommentId=41962
       */
      return [];
    }
  }

  async deleteSymlink(id: string | number, dir: string | number) {
    try {
      await API.deleteData(API_URL.FILES.deleteSymlink(projectsDataStore.projectId, id, dir));
      await this.fetchRelations();
    } catch (error) {
      throw error;
    }
  }

  async editFileDetails(value: {
    [key in keyof Pick<API_FileDataEntity, "description" | "name" | "index">]: string;
  }) {
    try {
      await API.editData(API_URL.FILES.updateFileDetails(projectsDataStore.projectId, this.fileId), value);
      runInAction(() => {
        this.data = { ...this.data, ...value };
      });
    } catch (error) {
      throw error;
    }
  }

  async openWebdavLink() {
    webDavStore.openFile(this.fileId);
  }

  setFilename(str: string) {
    this.filename = str;
  }

  handleError(err: FetchError) {
    this.stateError = err;
  }

  handlePreviewError() {
    this.previewError = true;
  }
}

export const fileDetailsStore = new FileDetailsStore();

// when file details is opened from dashboard - we init projects store to use project id in endpoints
// when file details modal gets closed - reset project id.
reaction(
  () => fileDetailsStore.from === "dashboard" && !fileDetailsStore.isVisible,
  (truly) => {
    if (truly) {
      projectsDataStore.resetProjectId();
    }
  }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).fileDetails = fileDetailsStore;
